import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { FiArrowRight, FiArrowUpRight } from 'react-icons/fi';
import { CATEGORIES, PRODUCTS, byCategory } from '../data/catalog';
import Reel from '../components/Reel';

// The catalogue reads as an index, not a wall of cards: a numbered row per product
// with the facts a buyer actually sorts on — platform, year, price — and one shared
// preview pane that follows whichever row you are on. Six identical cards told you
// nothing; a list you can scan tells you what the studio has made.

const IndexRow = ({ product, n, onFocus, active }) => {
  // Not everything we make has a page here. A web product with its own site links
  // straight out rather than to a stub, so a row never promises a page that is not
  // there.
  const rowProps = {
    onMouseEnter: () => onFocus(product),
    onFocus: () => onFocus(product),
    className: `group grid grid-cols-[auto_1fr_auto] items-center gap-x-5 gap-y-1 border-b border-border py-5 transition-colors sm:grid-cols-[2.5rem_auto_1fr_8rem_7rem] ${
      active ? 'bg-elevated/60' : 'hover:bg-elevated/40'
    }`,
  };
  const Row = product.link ? Link : 'a';
  const target = product.link
    ? { to: product.link }
    : { href: product.siteLink, target: '_blank', rel: 'noopener noreferrer' };

  return (
  <Row {...rowProps} {...target}>
    <span className="hidden font-mono text-xs text-muted tabular sm:block">
      {String(n).padStart(2, '0')}
    </span>

    <img
      src={product.iconSrc}
      alt=""
      width="40"
      height="40"
      loading="lazy"
      className="h-10 w-10 shrink-0 rounded-md border border-border object-cover"
    />

    <span className="min-w-0">
      <span className="block truncate font-display text-lg font-semibold text-primary group-hover:text-white">
        {product.name}
      </span>
      <span className="block truncate text-sm text-muted">{product.tagline}</span>
    </span>

    <span className="hidden font-mono text-xs text-muted sm:block">
      {product.platform}
      {product.status && (
        <span className="ml-2 border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-signal">
          {product.status}
        </span>
      )}
    </span>

    <span className="flex items-center justify-end gap-2 font-mono text-sm text-primary tabular">
      {product.price}
      <SafeIcon
        icon={FiArrowUpRight}
        className="h-4 w-4 text-muted transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-ember-bright"
      />
    </span>
  </Row>
  );
};

const Home = () => {
  const [preview, setPreview] = useState(PRODUCTS[0]);

  // Numbering follows the order the rows are actually rendered in. Using the
  // catalogue array position instead made the column read 05, 07, 06, because
  // the shelves are grouped by category and the array is not.
  const ordered = CATEGORIES.flatMap((cat) => byCategory(cat.id));

  return (
    <div className="flex flex-col">
      {/* Hero. Left-aligned and typographic: the art does the shouting, further down. */}
      <section className="border-b border-border">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-24 sm:px-6 md:pb-20 md:pt-28 lg:grid-cols-[1fr_16rem] lg:gap-20 lg:px-8">
          <div>
            <p className="label">Eager Dragon Publishing Ltd · London · est. 2023</p>
            <h1 className="mt-6 max-w-4xl font-display text-5xl font-extrabold leading-[0.95] tracking-tightest text-white md:text-7xl">
              We make games, desktop software and browser tools.
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-muted">
              A small studio shipping to the Microsoft Store and the Chrome Web Store. Everything we
              release is ad-free, tracker-free, and small enough to open instantly.
            </p>
            <div className="mt-10 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Link
                to="/products"
                className="flex items-center gap-2 bg-ember px-7 py-3 font-medium text-white transition-colors hover:bg-ember-bright"
              >
                See the catalogue <SafeIcon icon={FiArrowRight} />
              </Link>
              <Link
                to="/support"
                className="border border-border px-7 py-3 font-medium text-primary transition-colors hover:border-muted"
              >
                Get support
              </Link>
            </div>
          </div>

          {/* The facts a studio is judged on, counted from the catalogue itself so
              they cannot drift as products are added. */}
          <dl className="self-end divide-y divide-border border-y border-border font-mono text-sm lg:border-t-0">
            <div className="flex items-baseline justify-between gap-4 py-4">
              <dt className="label">Shipped</dt>
              <dd className="tabular text-2xl text-white">{String(PRODUCTS.length).padStart(2, '0')}</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-4">
              <dt className="label">Storefronts</dt>
              <dd className="tabular text-2xl text-white">02</dd>
            </div>
            <div className="flex items-baseline justify-between gap-4 py-4">
              <dt className="label">Trackers</dt>
              <dd className="tabular text-2xl text-ember-bright">00</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* The reel. Real frames from real builds. */}
      <section className="border-b border-border bg-card/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <p className="rule-label mb-6">Reel — frames from shipped builds</p>
          <Reel />
        </div>
      </section>

      {/* Catalogue index with a shared preview pane. */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-[1fr_22rem] lg:gap-16">
            <div>
              {CATEGORIES.map((cat) => {
                const items = byCategory(cat.id);
                if (!items.length) return null;
                return (
                  <div key={cat.id} id={cat.id} className="mb-14 scroll-mt-28 last:mb-0">
                    <div className="rule-label mb-2">
                      {cat.name}
                      <span className="tabular">{String(items.length).padStart(2, '0')}</span>
                    </div>
                    <p className="mb-6 max-w-xl text-sm text-muted">{cat.blurb}</p>
                    <div className="border-t border-border">
                      {items.map((product, index) => (
                        <IndexRow
                          key={product.id}
                          product={product}
                          n={ordered.indexOf(product) + 1}
                          onFocus={setPreview}
                          active={preview.id === product.id}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Preview pane. Hidden below lg, where each row already shows its icon
                and tapping simply opens the product. */}
            <aside className="hidden lg:block">
              <div className="sticky top-28">
                <div className="overflow-hidden border border-border bg-card">
                  <img
                    key={preview.id}
                    src={preview.image}
                    alt={`${preview.name} preview`}
                    className="aspect-[16/10] w-full object-cover"
                  />
                  <div className="border-t border-border p-5">
                    <div className="font-display text-lg font-semibold text-white">{preview.name}</div>
                    <p className="mt-2 text-sm leading-relaxed text-muted">{preview.short}</p>
                    <dl className="mt-5 space-y-2 font-mono text-xs">
                      <div className="flex justify-between gap-4">
                        <dt className="text-muted">Platform</dt>
                        <dd className="text-primary">{preview.platform}</dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-muted">Released</dt>
                        <dd className="text-primary tabular">{preview.year}</dd>
                      </div>
                      <div className="flex justify-between gap-4">
                        <dt className="text-muted">Price</dt>
                        <dd className="text-primary tabular">{preview.priceNote}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* House rules. Three statements on rules, not three icons in circles. */}
      <section>
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <p className="rule-label mb-10">House rules</p>
          <dl className="grid gap-x-12 gap-y-10 md:grid-cols-3">
            <div>
              <dt className="font-display text-xl font-semibold text-white">No ads, no trackers</dt>
              <dd className="mt-3 text-sm leading-relaxed text-muted">
                No banner ads, no ad networks and no third-party analytics in anything we ship.
              </dd>
            </div>
            <div>
              <dt className="font-display text-xl font-semibold text-white">Pay once where we can</dt>
              <dd className="mt-3 text-sm leading-relaxed text-muted">
                Every game and desktop app is a one-time purchase. PromptWick runs a server for each
                rewrite, so it keeps a free tier and a pay-once option alongside its plans.
              </dd>
            </div>
            <div>
              <dt className="font-display text-xl font-semibold text-white">Your data stays yours</dt>
              <dd className="mt-3 text-sm leading-relaxed text-muted">
                Desktop apps keep your library on your own machine. PromptWick needs an account to
                sync prompts and never sells what you write.{' '}
                <Link to="/privacy" className="text-primary underline decoration-border underline-offset-4 hover:decoration-ember">
                  Read the policy
                </Link>
                .
              </dd>
            </div>
          </dl>
        </div>
      </section>
    </div>
  );
};

export default Home;
